import OrDivider from 'components/common/recommendation-tiles/or-divider';
import WantColumnTitle from 'components/common/recommendation-tiles/want-column-title';
import WantFuelPicker from 'components/common/recommendation-tiles/want-fuel-picker';
import WantPickerReadout from 'components/common/recommendation-tiles/want-picker-readout';
import WantPickerSection from 'components/common/recommendation-tiles/want-picker-section';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  blowerOptionsForDwelling,
  COIL_PICKER_OPTIONS,
  OUTDOOR_PICKER_OPTIONS,
  PACKAGED_COOLING_PICKER_OPTIONS,
  PACKAGED_HEATING_PICKER_OPTIONS,
  visiblePickerOptions
} from 'utils/component-picker-options';
import { isFuelPickAction } from 'utils/composition-side';
import { BlowerDwelling } from 'utils/recommendation-tiles-logic';
import { SystemTypeOption } from 'utils/recommendations-strip-logic';
import {
  applyCompositionPick,
  buildCompositionIndex,
  compositionFuel,
  compositionIsElectricOnly,
  picksForSystemTypeId,
  resolvePickedSystemType,
  withFallbackFuel
} from 'utils/system-composition';
import {
  BlowerComponentId,
  CoilComponentId,
  CompositionPickAction,
  CompositionPicks,
  FurnaceFuel,
  OutdoorComponentId,
  PackagedCoolingId,
  PackagedHeatingId
} from 'utils/system-composition-types';

type WantComponentPickerProps = {
  // The full system-type universe (desiredOptions ∪ context.systemTypes)
  // the composition index builds from.
  catalog: SystemTypeOption[];
  // The strip's want selection. Tiles is single-want: the strip seeds,
  // restores, and fetches at most wantIds[0], and this picker reflects
  // that id's components. Re-syncs when the id changes externally
  // (seed/restore/prune); its own picks emit [resolvedId] or [].
  wantIds: number[];
  // Structure dwelling mode filtering the blower offer (same Geoff ruling
  // as the have canvas): mobile never offers the standard Furnace, standard
  // never offers the Mobile Home Furnace (unless Electric fuel is in play).
  dwelling?: BlowerDwelling;
  // Rendered when the catalog has no indexable types (old API / empty
  // catalog) — the picker vocabulary would be all-disabled noise.
  emptyPlaceholder: string;
  onWantIdsChange: (wantIds: number[]) => void;
  onPicksChange?: (picks: CompositionPicks, wantIds: number[]) => void;
  // The single fuel question on this card. The host writes the shared
  // ductedFilters.fuelSelections from this so Choose Systems and the
  // recommended-list / compatible-count narrowing follow the same pick.
  // Null is emitted for an explicit fuel-radio clear, and for a
  // composition with no combustion side at all — anywhere else an empty
  // restore must not wipe an equipment seed.
  onBlowerFuelChange?: (fuel: FurnaceFuel | null) => void;
  fallbackFuel?: FurnaceFuel | null;
};

// The "What you want" component picker (ported from the
// system-selector-frontend ducted "System you want" stage): instead of the
// 80+ system-type tile list, the user picks components per slot — fuel,
// outdoor unit, blower, coil, or a packaged unit — and the composition
// logic resolves the picks to the single catalog system type they form.
// Picks live here; the strip only sees a 0-or-1 wantIds. External changes
// (seeding, restore, prune) re-sync the picks via the last-synced-key
// guard, which also keeps this component's own emits from bouncing back.
export default function WantComponentPicker({
  catalog,
  wantIds,
  dwelling,
  emptyPlaceholder,
  onWantIdsChange,
  onPicksChange,
  onBlowerFuelChange,
  fallbackFuel = null
}: WantComponentPickerProps) {
  const index = useMemo(() => buildCompositionIndex(catalog), [catalog]);
  const [picks, setPicks] = useState<CompositionPicks>(() =>
    withFallbackFuel(picksForSystemTypeId(wantIds[0], index), fallbackFuel)
  );
  const wantKey = wantIds.join(',');
  const lastSyncedKeyRef = useRef(wantKey);
  const onBlowerFuelChangeRef = useRef(onBlowerFuelChange);
  onBlowerFuelChangeRef.current = onBlowerFuelChange;
  const onPicksChangeRef = useRef(onPicksChange);
  onPicksChangeRef.current = onPicksChange;
  const fallbackFuelRef = useRef(fallbackFuel);
  fallbackFuelRef.current = fallbackFuel;

  useEffect(() => {
    if (wantKey === lastSyncedKeyRef.current) return;
    lastSyncedKeyRef.current = wantKey;
    const nextPicks = withFallbackFuel(
      picksForSystemTypeId(wantIds[0], index),
      fallbackFuelRef.current
    );
    setPicks(nextPicks);
    const nextFuel = compositionFuel(nextPicks);
    if (nextFuel !== null) {
      onBlowerFuelChangeRef.current?.(nextFuel);
    }
  }, [wantKey, wantIds, index]);

  useEffect(() => {
    const resolved = resolvePickedSystemType(picks, index);
    onPicksChangeRef.current?.(picks, resolved ? [resolved.id] : []);
    const fuel = compositionFuel(picks);
    if (fuel !== null) {
      onBlowerFuelChangeRef.current?.(fuel);
    } else if (compositionIsElectricOnly(picks)) {
      // The one empty restore that MUST reach the shared filter: an
      // electric-only composition buckets under Electric, so a leftover gas
      // selection would narrow the very system being composed out of the
      // fetch.
      onBlowerFuelChangeRef.current?.(null);
    }
  }, [picks, index]);

  if (index.picksById.size === 0) {
    return (
      <div className='py-0.5 text-xs text-gray-400'>{emptyPlaceholder}</div>
    );
  }

  const resolved = resolvePickedSystemType(picks, index);

  const handlePick = (action: CompositionPickAction) => {
    const nextPicks = applyCompositionPick(picks, action);
    setPicks(nextPicks);
    const nextFuel = compositionFuel(nextPicks);
    if (nextFuel !== compositionFuel(picks)) {
      const allowClear =
        isFuelPickAction(action) || compositionIsElectricOnly(nextPicks);
      if (nextFuel !== null || allowClear) {
        onBlowerFuelChangeRef.current?.(nextFuel);
      }
    }
    const nextResolved = resolvePickedSystemType(nextPicks, index);
    const nextWantIds = nextResolved ? [nextResolved.id] : [];
    const nextKey = nextWantIds.join(',');
    if (nextKey === wantKey) return;
    lastSyncedKeyRef.current = nextKey;
    onWantIdsChange(nextWantIds);
  };

  return (
    <div data-testid='want-component-picker' className='flex flex-col gap-3'>
      <div className='flex flex-col gap-3 md:flex-row'>
        <div
          data-testid='want-split-system-picks'
          className='flex min-w-0 flex-1 flex-col gap-3'
        >
          <WantColumnTitle>Split System</WantColumnTitle>
          <WantFuelPicker
            side='split'
            picks={picks}
            index={index}
            onPick={handlePick}
          />
          <WantPickerSection<OutdoorComponentId>
            heading='Outdoor unit'
            options={visiblePickerOptions(
              OUTDOOR_PICKER_OPTIONS,
              picks.outdoor
            )}
            selectedId={picks.outdoor}
            picks={picks}
            index={index}
            actionFor={(id) => ({ slot: 'outdoor', option: id })}
            onPick={handlePick}
          />
          <WantPickerSection<BlowerComponentId>
            heading='Furnace or air mover'
            options={blowerOptionsForDwelling(
              dwelling ?? 'standard',
              picks.blower,
              picks.blowerFuel
            )}
            selectedId={picks.blower}
            picks={picks}
            index={index}
            actionFor={(id) => ({ slot: 'blower', option: id })}
            onPick={handlePick}
          />
          <WantPickerSection<CoilComponentId>
            heading='Evaporator coil'
            options={visiblePickerOptions(COIL_PICKER_OPTIONS, picks.coil)}
            selectedId={picks.coil}
            picks={picks}
            index={index}
            actionFor={(id) => ({ slot: 'coil', option: id })}
            onPick={handlePick}
          />
        </div>
        <OrDivider />
        <div
          data-testid='want-packaged-unit-picks'
          className='flex min-w-0 flex-1 flex-col gap-3'
        >
          <WantColumnTitle>Packaged Unit</WantColumnTitle>
          <WantFuelPicker
            side='packaged'
            picks={picks}
            index={index}
            onPick={handlePick}
          />
          <WantPickerSection<PackagedCoolingId>
            heading='Cooling'
            options={visiblePickerOptions(
              PACKAGED_COOLING_PICKER_OPTIONS,
              picks.packagedCooling
            )}
            selectedId={picks.packagedCooling}
            picks={picks}
            index={index}
            actionFor={(id) => ({ slot: 'packaged-cooling', option: id })}
            onPick={handlePick}
          />
          <WantPickerSection<PackagedHeatingId>
            heading='Heating'
            options={visiblePickerOptions(
              PACKAGED_HEATING_PICKER_OPTIONS,
              picks.packagedHeating
            )}
            selectedId={picks.packagedHeating}
            picks={picks}
            index={index}
            actionFor={(id) => ({ slot: 'packaged-heating', option: id })}
            onPick={handlePick}
          />
        </div>
      </div>
      <WantPickerReadout resolved={resolved} picks={picks} />
    </div>
  );
}
